import { useEffect, useMemo, useRef, useState } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { api } from "../lib/api";
import { MapPin, Loader2, Search, Phone, Mail, LocateFixed, AlertTriangle } from "lucide-react";

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
function distKm(a, b) {
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
const fmtKm = (d) => (d == null ? "" : `${d < 10 ? d.toFixed(1) : Math.round(d)} km`);

function cardHtml(p) {
  const name = escapeHtml(p.nome || "Installatore Partner");
  const loc = escapeHtml([p.comune, p.provincia].filter(Boolean).join(" · "));
  const addr = escapeHtml(p.indirizzo || "");
  const dist = p._dist != null ? `<span style="display:inline-block;margin-left:6px;padding:1px 7px;border-radius:20px;background:#eaf2ff;color:#2563EB;font-size:11px;font-weight:700">a ${fmtKm(p._dist)}</span>` : "";
  const initial = (p.nome || "?").trim().charAt(0).toUpperCase();
  const logo = p.logo_url
    ? `<img src="${escapeHtml(p.logo_url)}" alt="" style="width:44px;height:44px;border-radius:8px;object-fit:contain;background:#fff;border:1px solid #eee" onerror="this.style.display='none'"/>`
    : `<div style="width:44px;height:44px;border-radius:8px;background:#F4B400;color:#111;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:18px">${escapeHtml(initial)}</div>`;
  const tel = (p.telefono || "").trim();
  const mail = (p.email || "").trim();
  const btn = "display:inline-flex;align-items:center;gap:5px;padding:7px 12px;border-radius:8px;font-size:12px;font-weight:700;text-decoration:none;";
  const telBtn = tel ? `<a href="tel:${escapeHtml(tel.replace(/\s/g, ""))}" style="${btn}background:#F4B400;color:#111">Chiama</a>` : "";
  const mailBtn = mail ? `<a href="mailto:${escapeHtml(mail)}" style="${btn}background:#111;color:#fff">Email</a>` : "";
  return `<div style="font-family:system-ui,-apple-system,sans-serif;max-width:270px;padding:2px 2px 4px">
    <div style="display:flex;gap:11px;align-items:center;margin-bottom:8px">
      ${logo}
      <div style="min-width:0">
        <div style="font-size:14px;font-weight:800;color:#111;line-height:1.15">${name}</div>
        <div style="font-size:12px;color:#666;margin-top:2px">${loc}${dist}</div>
      </div>
    </div>
    ${addr ? `<div style="font-size:11.5px;color:#555;margin-bottom:9px">${addr}</div>` : ""}
    <div style="font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#999;margin-bottom:5px">Contattaci</div>
    <div style="display:flex;gap:8px;flex-wrap:wrap">${telBtn}${mailBtn}${!tel && !mail ? '<span style="font-size:12px;color:#999">Contatti non disponibili</span>' : ""}</div>
  </div>`;
}

function Pins({ points, selected, onSelect, centerPos }) {
  const map = useMap();
  const markersRef = useRef({});
  const infoRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google;
    const info = infoRef.current || (infoRef.current = new g.maps.InfoWindow());
    const ms = {};
    const bounds = new g.maps.LatLngBounds();
    (points || []).forEach((p) => {
      if (p.lat == null || p.lon == null) return;
      const m = new g.maps.Marker({
        position: { lat: p.lat, lng: p.lon }, map, title: p.nome,
        icon: { path: g.maps.SymbolPath.CIRCLE, scale: 7, fillColor: "#F4B400", fillOpacity: 1, strokeColor: "#1a1a1a", strokeWeight: 1.5 },
      });
      m.addListener("click", () => { info.setContent(cardHtml(p)); info.open(map, m); onSelect && onSelect(p.id); });
      ms[p.id] = m;
      bounds.extend({ lat: p.lat, lng: p.lon });
    });
    if (centerPos) {
      const um = new g.maps.Marker({
        position: { lat: centerPos.lat, lng: centerPos.lon }, map, title: centerPos.label || "Posizione", zIndex: 999,
        icon: { path: g.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#2563EB", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5 },
      });
      ms.__center__ = um;
      bounds.extend({ lat: centerPos.lat, lng: centerPos.lon });
    }
    markersRef.current = ms;
    let l;
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 64);
      l = g.maps.event.addListenerOnce(map, "idle", () => { if (map.getZoom() > 13) map.setZoom(13); });
    }
    return () => { Object.values(ms).forEach((m) => m.setMap(null)); if (infoRef.current) infoRef.current.close(); if (l) g.maps.event.removeListener(l); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, points, centerPos]);

  useEffect(() => {
    if (!map || !window.google || !selected) return;
    const m = markersRef.current[selected];
    const p = (points || []).find((x) => x.id === selected);
    if (m && p) {
      map.panTo({ lat: p.lat, lng: p.lon });
      if (map.getZoom() < 12) map.setZoom(13);
      if (infoRef.current) { infoRef.current.setContent(cardHtml(p)); infoRef.current.open(map, m); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, selected]);

  return null;
}

const selStyle = { appearance: "auto", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 13, color: "#111", background: "#fff", fontFamily: "system-ui, sans-serif", outline: "none", width: "100%" };
const RADIUS_KM = 50;

function ListItem({ p, active, onClick }) {
  const initial = (p.nome || "?").trim().charAt(0).toUpperCase();
  return (
    <button onClick={onClick} data-testid={`installer-list-item-${p.id}`}
      style={{ display: "flex", gap: 11, alignItems: "center", width: "100%", textAlign: "left", padding: "10px 12px", border: "none", borderBottom: "1px solid #f0f0f0", cursor: "pointer", background: active ? "#fff9e6" : "#fff", fontFamily: "system-ui, sans-serif" }}>
      {p.logo_url ? (
        <img src={p.logo_url} alt="" style={{ width: 38, height: 38, borderRadius: 8, objectFit: "contain", background: "#fff", border: "1px solid #eee", flexShrink: 0 }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div style={{ width: 38, height: 38, borderRadius: 8, background: active ? "#F4B400" : "#f1f1f1", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15, flexShrink: 0 }}>{initial}</div>
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.nome}</div>
        <div style={{ fontSize: 11.5, color: "#777", marginTop: 1 }}>{[p.comune, p.provincia].filter(Boolean).join(" · ")}</div>
      </div>
      {p._dist != null ? (
        <span style={{ fontSize: 11, fontWeight: 800, color: "#2563EB", background: "#eaf2ff", borderRadius: 20, padding: "2px 8px", flexShrink: 0 }} data-testid={`installer-dist-${p.id}`}>{fmtKm(p._dist)}</span>
      ) : (
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {p.telefono && <Phone size={14} color="#F4B400" />}
          {p.email && <Mail size={14} color="#888" />}
        </div>
      )}
    </button>
  );
}

export default function PublicInstallersMap() {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regione, setRegione] = useState("");
  const [provincia, setProvincia] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [narrow, setNarrow] = useState(typeof window !== "undefined" && window.innerWidth < 760);
  const [userPos, setUserPos] = useState(null);
  const [geoStatus, setGeoStatus] = useState("idle"); // idle | locating | ok | error | unsupported
  const [geoMsg, setGeoMsg] = useState("");
  const [searchPos, setSearchPos] = useState(null); // {lat, lon, label}
  const [searchStatus, setSearchStatus] = useState("idle"); // idle | searching | ok | notfound

  const locate = () => {
    if (!navigator.geolocation) { setGeoStatus("unsupported"); setGeoMsg("Il tuo browser non supporta la geolocalizzazione. Prova con un altro dispositivo o browser."); return; }
    setGeoStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserPos({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setGeoStatus("ok"); setGeoMsg(""); setSearchPos(null); setSearchStatus("idle"); setQ(""); setRegione(""); setProvincia(""); setSelected(null); },
      (err) => {
        setGeoStatus("error");
        if (err && err.code === 1) setGeoMsg("Hai negato l'accesso alla posizione. Nessun problema: usa i filtri Regione/Provincia oppure cerca la tua città qui sotto. Per attivare la posizione: icona lucchetto accanto all'indirizzo → Posizione → Consenti, poi «Riprova».");
        else if (err && err.code === 2) setGeoMsg("Non riusciamo a rilevare la tua posizione. Verifica che il GPS/localizzazione sia attivo, oppure usa i filtri o la ricerca città qui sotto.");
        else if (err && err.code === 3) setGeoMsg("La localizzazione sta impiegando troppo tempo. Riprova, oppure usa i filtri o la ricerca città qui sotto.");
        else setGeoMsg("Non è stato possibile ottenere la posizione. Usa i filtri o la ricerca città qui sotto.");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  };

  const doSearch = () => {
    const term = q.trim();
    if (!term) { setSearchPos(null); setSearchStatus("idle"); return; }
    if (!(window.google && window.google.maps)) { setSearchStatus("notfound"); return; }
    setSearchStatus("searching");
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: term, componentRestrictions: { country: "IT" } }, (res, status) => {
      if (status === "OK" && res && res[0]) {
        const loc = res[0].geometry.location;
        setSearchPos({ lat: loc.lat(), lon: loc.lng(), label: res[0].formatted_address });
        setSearchStatus("ok");
        setRegione(""); setProvincia(""); setUserPos(null); setGeoStatus("idle"); setSelected(null);
      } else {
        setSearchPos(null); setSearchStatus("notfound");
      }
    });
  };

  useEffect(() => {
    document.body.style.background = "#ffffff";
    api.get("/public/installers-map").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
    const on = () => setNarrow(window.innerWidth < 760);
    window.addEventListener("resize", on);
    locate(); // tentativo di geolocalizzazione automatica all'avvio
    return () => window.removeEventListener("resize", on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const regioni = useMemo(() => Array.from(new Set(items.map((i) => i.regione).filter(Boolean))).sort((a, b) => a.localeCompare(b, "it")), [items]);
  const province = useMemo(() => {
    const src = regione ? items.filter((i) => i.regione === regione) : items;
    return Array.from(new Set(src.map((i) => i.provincia).filter(Boolean))).sort((a, b) => a.localeCompare(b, "it"));
  }, [items, regione]);

  const centerPos = searchPos || userPos;

  const display = useMemo(() => {
    const base = items.filter((i) => (!regione || i.regione === regione) && (!provincia || i.provincia === provincia));
    if (searchPos) {
      return base
        .map((i) => ({ ...i, _dist: distKm(searchPos, { lat: i.lat, lon: i.lon }) }))
        .filter((i) => i._dist <= RADIUS_KM)
        .sort((a, b) => a._dist - b._dist);
    }
    if (regione || provincia) {
      const l = base.map((i) => (userPos ? { ...i, _dist: distKm(userPos, { lat: i.lat, lon: i.lon }) } : i));
      l.sort((a, b) => (userPos ? a._dist - b._dist : (a.nome || "").localeCompare(b.nome || "", "it")));
      return l;
    }
    if (userPos) {
      return base.map((i) => ({ ...i, _dist: distKm(userPos, { lat: i.lat, lon: i.lon }) })).sort((a, b) => a._dist - b._dist).slice(0, 3);
    }
    return [];
  }, [items, regione, provincia, searchPos, userPos]);

  const onRegione = (v) => { setRegione(v); setProvincia(""); setSearchPos(null); setSearchStatus("idle"); setQ(""); setSelected(null); };
  const onProvincia = (v) => { setProvincia(v); setSearchPos(null); setSearchStatus("idle"); setQ(""); setSelected(null); };
  const resetAll = () => { setRegione(""); setProvincia(""); setSearchPos(null); setSearchStatus("idle"); setQ(""); setSelected(null); };

  if (!apiKey) {
    return <div style={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center", color: "#555", fontFamily: "system-ui, sans-serif" }}>Mappa non disponibile (configurazione mancante).</div>;
  }

  const hasFilter = regione || provincia || searchPos;
  const countText = loading ? "Caricamento…"
    : searchPos ? `${display.length} partner entro ${RADIUS_KM} km`
    : (regione || provincia) ? `${display.length} installatori in zona`
    : userPos ? `I ${display.length} partner più vicini a te`
    : "Scegli una zona, cerca una città o attiva la posizione";

  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: narrow ? "column" : "row", background: "#fff" }} data-testid="public-installers-map">
      <aside style={{ width: narrow ? "auto" : 340, height: narrow ? "48%" : "100%", borderRight: narrow ? "none" : "1px solid #eee", borderBottom: narrow ? "1px solid #eee" : "none", display: "flex", flexDirection: "column", background: "#fff", minHeight: 0 }} data-testid="installers-sidebar">
        <div style={{ padding: "14px 14px 10px", borderBottom: "1px solid #f0f0f0" }} data-testid="installers-map-filters">
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 12 }}>
            <MapPin size={20} color="#F4B400" strokeWidth={2.4} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#111", lineHeight: 1.1, fontFamily: "system-ui, sans-serif" }}>Rete Installatori Partner</div>
              <div style={{ fontSize: 12, color: "#666", fontFamily: "system-ui, sans-serif" }} data-testid="installers-map-count">{countText}</div>
            </div>
          </div>

          <button onClick={locate} disabled={geoStatus === "locating"} data-testid="geo-locate-btn"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, width: "100%", padding: "9px 10px", marginBottom: 8, borderRadius: 8, border: "1px solid " + (userPos ? "#2563EB" : "#ddd"), background: userPos ? "#eaf2ff" : "#fff", color: userPos ? "#2563EB" : "#111", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "system-ui, sans-serif" }}>
            {geoStatus === "locating" ? <Loader2 className="animate-spin" size={15} /> : <LocateFixed size={15} />}
            {geoStatus === "locating" ? "Localizzazione…" : userPos ? "Aggiorna la mia posizione" : "Trova i 3 partner più vicini"}
          </button>

          {(geoStatus === "error" || geoStatus === "unsupported") && (
            <div role="alert" data-testid="geo-alert" style={{ display: "flex", gap: 9, alignItems: "flex-start", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 10, padding: "10px 11px", marginBottom: 10, fontFamily: "system-ui, sans-serif" }}>
              <AlertTriangle size={18} color="#DC2626" style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: "#991B1B", marginBottom: 2 }}>Posizione non rilevata</div>
                <div style={{ fontSize: 11.5, color: "#7f1d1d", lineHeight: 1.45 }}>{geoMsg || "Non è stato possibile ottenere la tua posizione."}</div>
                {geoStatus === "error" && (
                  <button onClick={locate} data-testid="geo-retry-btn" style={{ marginTop: 8, padding: "6px 12px", borderRadius: 8, border: "none", background: "#DC2626", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Riprova</button>
                )}
              </div>
            </div>
          )}

          {/* Ricerca per località (raggio 50 km) */}
          <form onSubmit={(e) => { e.preventDefault(); doSearch(); }} style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center", flex: 1 }}>
              <Search size={15} color="#999" style={{ position: "absolute", left: 10 }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca città o indirizzo…" style={{ ...selStyle, paddingLeft: 30 }} data-testid="filter-search" />
            </div>
            <button type="submit" data-testid="search-btn" style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#111", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "system-ui, sans-serif", flexShrink: 0 }}>
              {searchStatus === "searching" ? <Loader2 className="animate-spin" size={14} /> : "Cerca"}
            </button>
          </form>
          {searchStatus === "notfound" && <div style={{ fontSize: 11.5, color: "#b45309", marginBottom: 8, fontFamily: "system-ui, sans-serif" }} data-testid="search-notfound">Località non trovata. Prova con il nome del comune o un indirizzo più preciso.</div>}
          {searchPos && <div style={{ fontSize: 11, color: "#2563EB", marginBottom: 8, fontFamily: "system-ui, sans-serif" }} data-testid="search-center">Partner entro {RADIUS_KM} km da: {searchPos.label}</div>}

          <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: "#aaa", margin: "2px 0 6px", fontFamily: "system-ui, sans-serif" }}>oppure filtra per zona</div>
          <div style={{ display: "flex", gap: 8 }}>
            <select value={regione} onChange={(e) => onRegione(e.target.value)} style={selStyle} data-testid="filter-regione" aria-label="Regione">
              <option value="">Tutte le regioni</option>
              {regioni.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select value={provincia} onChange={(e) => onProvincia(e.target.value)} style={selStyle} data-testid="filter-provincia" aria-label="Provincia">
              <option value="">Tutte le province</option>
              {province.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {hasFilter && (
            <button onClick={resetAll} data-testid="filter-reset" style={{ marginTop: 8, padding: "6px 10px", borderRadius: 8, border: "1px solid #f0d38a", background: "#fff9e6", color: "#b45309", fontWeight: 700, fontSize: 12, cursor: "pointer", width: "100%", fontFamily: "system-ui, sans-serif" }}>Azzera</button>
          )}
        </div>

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }} data-testid="installers-list">
          {loading ? (
            <div style={{ padding: 24, display: "flex", justifyContent: "center" }}><Loader2 className="animate-spin" size={22} color="#F4B400" /></div>
          ) : display.length === 0 ? (
            (regione || provincia || searchPos) ? (
              <div style={{ padding: 20, fontSize: 13, color: "#888", fontFamily: "system-ui, sans-serif" }} data-testid="installers-list-empty">{searchPos ? `Nessun partner entro ${RADIUS_KM} km da qui.` : "Nessun installatore in questa zona."}</div>
            ) : (
              <div style={{ padding: 20, fontSize: 13, color: "#666", fontFamily: "system-ui, sans-serif", lineHeight: 1.5 }} data-testid="installers-list-nopos">Per trovare i partner: attiva la posizione, <b>cerca la tua città</b> (raggio {RADIUS_KM} km) oppure scegli Regione/Provincia.</div>
            )
          ) : (
            display.map((p) => <ListItem key={p.id} p={p} active={selected === p.id} onClick={() => setSelected(p.id)} />)
          )}
        </div>
      </aside>

      <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
        {loading && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 5 }}><Loader2 className="animate-spin" size={28} color="#F4B400" /></div>}
        <APIProvider apiKey={apiKey}>
          <Map defaultCenter={{ lat: 42.8, lng: 12.5 }} defaultZoom={6} gestureHandling="greedy" mapTypeId="roadmap" disableDefaultUI={false} mapTypeControl={false} streetViewControl={false} fullscreenControl={false} style={{ width: "100%", height: "100%" }}>
            <Pins points={display} selected={selected} onSelect={setSelected} centerPos={centerPos} />
          </Map>
        </APIProvider>
      </div>
    </div>
  );
}
